<template>
  <div v-scroll="onScroll">
    <v-img
      :src="galleryInfo.thumbnail || ''"
      :alt="``"
      class="primary main-gallery-image mb-3"
      cover
    >
      <template #placeholder>
        <v-row class="fill-height ma-0" align="center" justify="center">
          <v-progress-circular
            indeterminate
            color="tertiary"
          ></v-progress-circular>
        </v-row>
      </template>
      <v-btn
        v-if="hasFrom"
        class="my-3 ml-3 close-btn"
        color="secondary"
        absolute
        outlined
        small
        depressed
        @click="goBack"
      >
        <v-icon small class="mr-3">
          fal fa-long-arrow-left
        </v-icon>
        <span>Back</span>
      </v-btn>
    </v-img>
    <v-container v-if="mounted">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8" xl="6">
          <div class="text-center">
            <h1 class="text-h4 font-weight-bold">
              {{ galleryInfo.title }}
            </h1>
            <h2 class="text-h6 mb-3">
              by
              <nuxt-link
                :to="
                  `/photographer/${galleryOwner.userName || galleryOwner.id}`
                "
                class="no-underline font-weight-bold"
              >
                {{ galleryOwner.fullName }}
              </nuxt-link>
            </h2>
            <p class="text-body-1 mb-12">
              {{ galleryInfo.description }}
            </p>
            <Actions
              v-if="!galleryInfo.private"
              :me="me"
              :info="infoMe"
              :gallery="galleryInfo"
              :owner="galleryOwner"
            />
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid>
      <v-row>
        <v-col cols="12" class="px-0">
          <Gallery
            v-if="mounted"
            :photos="galleryPhotos"
            :me="me"
            :load-more="loadMore"
            :auto-load-disabled="autoLoadDisabled"
            :loading="loading"
            :get-queries="getQueries"
            :gallery-owner-id="galleryOwner.id"
            from="galleries"
          />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Gallery from '@/components/galleries/Gallery.vue'
import Actions from '@/components/galleries/gallery/Actions.vue'

export default {
  components: {
    Gallery,
    Actions
  },
  middleware: ['header'],
  async asyncData({ params, $axios, store, $asa }) {
    try {
      let seoData = {}
      seoData = await $axios.$get(`/galleries/${params.id}/seo`)
      return {
        seoData
      }
    } catch (error) {
      $asa.appInsights('Pages: galleries/_id.vue - method: asyncData', error)
    }
  },
  data() {
    return {
      mounted: false,
      loading: false,
      offsetTop: 0
    }
  },
  async fetch({ store, params, $asa }) {
    try {
      await store.dispatch('galleries/resetStore')
      const data = {
        id: params.id,
        page: 1
      }
      const lastPage = store.state.galleries.galleryPhotosPage
      await store.dispatch('photos/featuredInGalleriesDialogVisible', false)
      if (lastPage === -1) {
        await store.dispatch('galleries/getGallery', data)
      }
    } catch (error) {
      $asa.appInsights('Pages: galleries/_id.vue - method: fetch', error)
    }
  },
  head({ $seo }) {
    return $seo({
      title: `${this.seoData.title} by ${this.seoData.ownerFullName}`,
      description: `Find out which 100ASA's photos are the favourites of ${this.seoData.ownerFullName}.`,
      url: `https://100asa.com/galleries/${this.seoData.id}/`,
      image: this.seoData.thumbnail
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      galleryPhotos: state => state.galleries.galleryPhotos,
      galleryPhotosIsLastPage: state => state.galleries.galleryPhotosIsLastPage,
      galleryPhotosPage: state => state.galleries.galleryPhotosPage,
      galleryInfo: state => state.galleries.galleryInfo,
      galleryOwner: state => state.galleries.galleryOwner,
      infoMe: state => state.galleries.infoMe
    }),
    autoLoadDisabled() {
      return this.loading || !this.mounted || this.galleryPhotos.length === 0
    },
    hasFrom() {
      return !!this.$route.query.from
    }
  },
  async mounted() {
    const galleryId = this.$route.params.id
    if (galleryId) {
      try {
        if (this.me && this.me.id) {
          await this.$store.dispatch('galleries/getInfo', galleryId)
        }
      } catch (error) {
        this.$asa.appInsights('Pages: galleries/_id.vue - method: mounted', error)
      } finally {
        this.loading = false
        this.mounted = true
      }
    } else {
      this.goBack()
    }
  },
  methods: {
    getQueries() {
      return `&st=${this.offsetTop}&gid=${this.galleryInfo.id}`
    },
    goBack() {
      const q = this.$route.query
      let path

      switch (this.$route.query.from) {
        case 'photo':
          path = `/photo/${q.id}`
          break
        case 'photographer':
          path = `/photographer/${q.pid}?t=2`
          break
        case 'account':
          path = `/account/galleries`
          break
        default:
          path = '/'
          break
      }
      this.$router.push(path)
    },
    async loadMore() {
      if (!this.galleryPhotosIsLastPage && !this.loading && this.mounted) {
        try {
          this.loading = true
          const nextPage = this.galleryPhotosPage + 1
          const data = {
            id: this.$route.params.id,
            page: nextPage
          }
          await this.$store.dispatch('galleries/getGallery', data)
        } catch (error) {
          this.$asa.appInsights('Pages: galleries/_id.vue - method: loadMore', error)
        } finally {
          this.loading = false
        }
      }
    },
    onScroll() {
      setTimeout(() => {
        const top = document.getElementById('app').getBoundingClientRect().top * -1
        this.offsetTop = top
      })
    }
  }
}
</script>

<style scoped>
.main-gallery-image {
  height: 60vh;
}
</style>
